// Comparar un ruta contra otro.
//
// ── La medida que pidió el cliente ──
//
// «Kilos totales de queso ÷ tinas totales de leche». Es la que se habla: «en
// Rionegro la tina me está dando 5,4 y en La Ceja 5,8».
//
// ── Y por qué esa sola no alcanza ──
//
// La búfala rinde bastante más que la vaca. El centro al que le llega más
// búfala saca más kilos por tina SIN HACER NADA MEJOR, y comparando solo ese
// número el mejor centro sería el que tuvo suerte con el reparto de la leche.
//
// Por eso van los dos: los kilos por tina, que es lo que se habla, y el
// CUMPLIMIENTO —kilos reales contra los que la leche que entró debía dar—, que
// no depende de la mezcla y es el que de verdad compara.

import { LITROS_POR_TINA } from './tina';

const num = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

// La lectura de una ruta. `fila` viene del servidor.
export function lecturaDeRuta(fila) {
  if (!fila) return null;
  const litros = num(fila.litros);
  const kilos = num(fila.kilos);
  if (litros <= 0 || kilos <= 0) return null;

  const tinas = litros / LITROS_POR_TINA;
  const lb = num(fila.litros_bufala);

  // El cumplimiento se calcula SOLO sobre las cuajadas que se pueden juzgar
  // —las que tienen pactado el rendimiento de cada leche que entró—. Metiendo
  // las otras, el esperado quedaría corto y el cumplimiento saldría inflado:
  // la ruta con las fichas peor llenadas sería el que mejor cumple.
  const esperados = num(fila.kilos_esperados);
  const kilosMedibles = num(fila.kilos_medibles);
  const cumple = (esperados > 0 && kilosMedibles > 0)
    ? Math.round((kilosMedibles / esperados) * 1000) / 10
    : null;

  return {
    litros, kilos, tinas: Math.round(tinas * 10) / 10,
    porTina: Math.round((kilos / tinas) * 100) / 100,
    parteBufala: litros > 0 ? Math.round((lb / litros) * 100) : 0,
    cumple,
    // Cuántas quedaron fuera de la comparación, para poder decirlo en vez de
    // presentar un porcentaje que tapa la mitad de la producción.
    cuajadas: num(fila.cuajadas),
    medibles: num(fila.cuajadas_medibles),
    sinMedir: Math.max(0, num(fila.cuajadas) - num(fila.cuajadas_medibles)),
  };
}

// Ordenados por el que de verdad compara, y si no se puede, por kilos por tina.
//
// Sin cumplimiento se ordena por kilos por tina y NO se mezclan los dos
// criterios en una sola lista silenciosamente: el que no se puede medir va al
// final, no al medio disfrazado de bueno.
// LAS RUTAS SIN PRODUCCIÓN TAMBIÉN SALEN, al final y sin nota.
//
// Antes se filtraban. Y una ruta que desaparece de la lista no se lee como
// «esta ruta no cuajó en este periodo»: se lee como que el programa perdió una
// ruta, o como que uno la borró sin querer. Quien mira se queda buscándola en
// vez de mirando las que sí trabajaron.
//
// Es lo mismo que los días en cero de la relación: el cero es una afirmación,
// la ausencia es un hueco.
export function compararRutas(filas) {
  const leidos = (filas || []).map(f => ({ ...f, lectura: lecturaDeRuta(f) }));
  return leidos.sort((a, b) => {
    // Las que no produjeron van de últimas, siempre.
    if (!a.lectura !== !b.lectura) return a.lectura ? -1 : 1;
    if (!a.lectura) return String(a.nombre).localeCompare(String(b.nombre));
    const ca = a.lectura.cumple, cb = b.lectura.cumple;
    if (ca !== null && cb !== null) return cb - ca;
    if (ca !== null) return -1;
    if (cb !== null) return 1;
    return b.lectura.porTina - a.lectura.porTina;
  });
}

// ¿Vale la pena comparar? Con un solo centro no hay nada que comparar, y una
// tabla de comparación de una fila es una tabla que sobra.
export function valeCompararlas(filas) {
  return (filas || []).filter(f => num(f.kilos) > 0).length > 1;
}

// ── La calificación de una ruta ──────────────────────────────────────────
//
// Sale del CUMPLIMIENTO —kilos reales contra los que la leche que entró debía
// dar—, no de los kilos por tina. Los kilos por tina premiarían a la ruta a la
// que le tocó más búfala, que no hizo nada mejor: le repartieron mejor leche.
//
// Las franjas no son simétricas a propósito. Rendir un 2% por debajo de lo
// esperado es normal entre una cuajada y otra; un 10% por debajo no se explica
// solo con el azar, y ahí hay que ir a mirar. Poner el rojo en el 98% haría que
// todas las rutas salieran en rojo casi siempre, y un tablero rojo entero no
// dice nada.
export const FRANJAS = [
  { desde: 100, nombre: 'Excelente', color: 'var(--bueno)',  peso: 4 },
  { desde: 96,  nombre: 'Bien',      color: 'var(--venta)',  peso: 3 },
  { desde: 90,  nombre: 'Ojo',       color: 'var(--ojo)',    peso: 2 },
  { desde: -Infinity, nombre: 'Bajo', color: 'var(--malo)',  peso: 1 },
];

export function calificar(cumple) {
  // Sin cumplimiento no se inventa una nota. Una ruta sin medir no es una ruta
  // mala: es una a la que le faltan los rendimientos pactados, y ponerle un
  // color la acusa de algo que nadie comprobó.
  if (cumple === null || cumple === undefined || !Number.isFinite(Number(cumple))) {
    return { nombre: 'Sin medir', color: 'var(--tinta-tenue)', peso: 0, cumple: null };
  }
  const n = Number(cumple);
  const f = FRANJAS.find(x => n >= x.desde);
  return { ...f, cumple: n };
}

// Qué tan llena va la barra. El 100% es lo esperado, no el tope: una ruta que
// cumple al 104% llena la barra entera y no se sale.
export function llenado(cumple, tope = 105) {
  if (cumple === null || !Number.isFinite(Number(cumple))) return 0;
  // Desde el 80% para abajo la barra ya está vacía. Empezando en cero, todas
  // las rutas se verían casi llenas y las diferencias no se notarían.
  const piso = 80;
  const n = Math.max(piso, Math.min(tope, Number(cumple)));
  return Math.round(((n - piso) / (tope - piso)) * 100);
}

// ── El día a día de una ruta ─────────────────────────────────────────────
//
// El promedio del periodo dice cómo va la ruta; el día a día dice QUÉ DÍA se
// torció. Un 95% en el mes puede ser veintinueve días clavados y uno malo, o
// treinta días flojos: son dos problemas distintos, con causas distintas, y el
// promedio solo no los separa.
export function diasDeRuta(filas, centroId) {
  return (filas || [])
    .filter(f => f && f.centro_id === centroId)
    .map(f => {
      const l = lecturaDeRuta(f);
      return l ? { fecha: String(f.fecha).slice(0, 10), ...l } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.fecha.localeCompare(a.fecha));
}

// Cuánto se mueve una ruta de un día a otro.
//
// Dos rutas con el mismo promedio no son iguales: la que va clavada todos los
// días es un proceso que anda, y la que sube y baja es uno que no se controla
// —aunque el promedio salga igual—. Eso es lo que no dice una cifra sola.
export function variacion(dias) {
  const conNota = (dias || []).filter(d => d.cumple !== null);
  if (conNota.length < 2) return null;
  const notas = conNota.map(d => d.cumple);
  const peor = Math.min(...notas), mejor = Math.max(...notas);
  return {
    dias: conNota.length,
    peor, mejor,
    horquilla: Math.round((mejor - peor) * 10) / 10,
    // El día flojo que vale la pena ir a mirar. Se nombra uno, no una lista:
    // una lista de días malos se lee como un regaño y no se revisa ninguno.
    peorDia: conNota.find(d => d.cumple === peor),
  };
}
